import { ClientError } from './ClientError';
import { NotFoundError } from './NotFoundError';
import { SessionExpirationError } from './SessionExpirationError';

import { ErrorMessage } from '../ErrorMessage';
import { DEFAULT_ERROR_MESSAGE } from '../constants';

type RequestContext = { uri: uri.URI; req: RequestInit };
type RequestMiddleware = (req: RequestInit) => RequestInit;
type ResponseMiddleware = (res: Promise<Response>, ctx: RequestContext) => Promise<Response>;
type MultiMiddleware = {
  req: RequestMiddleware;
  res: ResponseMiddleware;
};

const extractResponseErrors = (responseBody: any) => {
  const errorMessages: ErrorMessage[] = [];

  if (responseBody.errors) {
    responseBody.errors.forEach((error: any) => {
      if (error.title) {
        const sourcePath = error.source && error.source.pointer && error.source.pointer.split('/');
        const property = sourcePath ? sourcePath[sourcePath.length - 1] : undefined;
        errorMessages.push({ property, message: error.title });
      }
    });
  }

  if (errorMessages.length === 0) {
    errorMessages.push({ message: DEFAULT_ERROR_MESSAGE });
  }

  return errorMessages;
};

const raise = (response: Response, body: object, ctx: RequestContext) => {
  const hasBody = !!body && Object.keys(body).length > 0;
  if (response.status === 401 && !hasBody) {
    throw new SessionExpirationError();
  }
  if (response.status === 404) {
    throw new NotFoundError(ctx.uri.path());
  }

  throw new ClientError(response.statusText, extractResponseErrors(body));
};

const statusOK = (): ResponseMiddleware => (response, ctx) =>
  response.then((res) => {
    if (res.status >= 200 && res.status < 399) {
      return Promise.resolve(res);
    }

    return res
      .json()
      .catch(() => Promise.resolve({}))
      .then((body) => raise(res, body, ctx));
  });

const lookupRelationship = (relation: any, record: any, included: any): any[] => {
  if (
    !record.relationships ||
    !record.relationships[relation] ||
    !record.relationships[relation].data ||
    !included
  ) {
    return [];
  }

  const relatedRecordIds =
    record.relationships[relation].data instanceof Array
      ? record.relationships[relation].data.map((linkage: any) => linkage.id)
      : [record.relationships[relation].data.id];

  return relatedRecordIds
    .map((linkage: any) =>
      included.find((includedRecord: any) => {
        return includedRecord.id === linkage;
      }),
    )
    .filter((r: any) => !!r);
};

const authorize =
  (accessToken: string): RequestMiddleware =>
  (req) => ({
    ...req,
    headers: { ...req.headers, Authorization: `Bearer ${accessToken}` },
  });

const jsonapi = (): MultiMiddleware => ({
  req: (req) => {
    if (req.body && req.body.constructor === FormData) {
      return req;
    } else {
      const newReq: any = {
        ...req,
        headers: { ...req.headers, Accept: 'application/vnd.api+json' },
      };
      if (req.body) {
        newReq.headers['Content-Type'] = 'application/vnd.api+json';
      }
      return newReq;
    }
  },
  res: (res) =>
    res.then((r) => {
      const contentType = r.headers.get('content-type');
      if (contentType && /json/.test(contentType)) {
        return r.json();
      }

      return r;
    }),
});

class Client {
  private requestMiddleware: RequestMiddleware[] = [];
  private responseMiddleware: ResponseMiddleware[] = [];

  constructor(public host: uri.URI) {}

  get = (path: string, params?: object) => this.request(path, { method: 'GET' }, params);
  post = (path: string, body: string | RequestInit['body'] | object, params?: object) => {
    const postBody = body && body.constructor === FormData ? body : JSON.stringify(body);
    return this.request(path, { method: 'POST', body: postBody }, params);
  };
  patch = (path: string, body: object, params?: object) =>
    this.request(path, { method: 'PATCH', body: JSON.stringify(body) }, params);
  delete = (path: string, params?: object) => this.request(path, { method: 'DELETE' }, params);

  res = (middleware: ResponseMiddleware | MultiMiddleware) => {
    const mid = typeof middleware === 'function' ? middleware : middleware.res;
    this.responseMiddleware.push(mid);
    return this;
  };

  req = (middleware: RequestMiddleware | MultiMiddleware) => {
    const mid = typeof middleware === 'function' ? middleware : middleware.req;
    this.requestMiddleware.push(mid);
    return this;
  };

  private request = (path: string, requestConfig: object, params?: object): Promise<Response> =>
    new Promise((resolve, reject) => {
      const requestURI = this.host.clone();
      requestURI.path(path);
      if (typeof params === 'object') {
        requestURI.setQuery(params);
      }
      const finalConfig = this.requestMiddleware.reduce((c, m) => m(c), requestConfig);

      const promise = fetch(requestURI.toString(), finalConfig);

      const ctx = { uri: requestURI, req: finalConfig };
      this.responseMiddleware.reduce((p, m) => m(p, ctx), promise).then(resolve, reject);
    });
}

export default Client;
export {
  jsonapi,
  statusOK,
  lookupRelationship,
  authorize,
  ClientError,
  NotFoundError,
  SessionExpirationError,
};
